import { useCallback, useEffect, useRef, useState } from 'react'
import { AgamiLibraryResponse } from '../types/agami-library'
import { AgamiLibraryQuery } from '../utils/helpers/agami-library.helpers'
import { supabase } from '../utils/helpers/supabase.helpers'

interface UseUserAgamiLibraryResult {
  data: AgamiLibraryResponse | null
  resolvedRequestPage: number | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

export const useUserAgamiLibrary = (query: AgamiLibraryQuery, enabled = true): UseUserAgamiLibraryResult => {
  const [data, setData] = useState<AgamiLibraryResponse | null>(null)
  const [resolvedRequestPage, setResolvedRequestPage] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const latestRequest = useRef(0)

  const refetch = useCallback(() => setRefreshKey((value) => value + 1), [])

  useEffect(() => {
    if (!enabled) return

    latestRequest.current += 1
    const requestId = latestRequest.current
    setIsLoading(true)
    setError(null)

    const fetchLibrary = async () => {
      const { data: payload, error: rpcError } = await supabase.rpc('get_user_agami_library', {
        p_page: query.page,
        p_page_size: 25,
        p_search: query.search || null,
        p_status: query.status,
        p_activation_type: query.activationType,
        p_media_type: query.mediaType,
        p_language: query.language,
        p_sort: query.sort,
      })

      if (requestId !== latestRequest.current) return

      if (rpcError) {
        setError(rpcError.message)
      } else {
        setData(payload as unknown as AgamiLibraryResponse)
        setResolvedRequestPage(query.page)
      }
      setIsLoading(false)
    }

    fetchLibrary()
  }, [
    enabled,
    query.activationType,
    query.language,
    query.mediaType,
    query.page,
    query.search,
    query.sort,
    query.status,
    refreshKey,
  ])

  return { data, resolvedRequestPage, isLoading, error, refetch }
}
